// ============================================================================
// derivePersonRole — role AFFICHE derive (D3, MASTER-GEST-FIX-DECISIONS 2026-07-24)
// ============================================================================
// Source UNIQUE de derivation pour toute surface qui affiche une personne.
// Le role derive FAIT AUTORITE ; job_title (titre libre) reste un complement
// facultatif cote UI — jamais a la place de ce role.
//
// Precedence (STOP-8) :
//   1. systemCRole === 'ADMIN'              → admin     (Administrateur)
//   2. isTeamDirector (Teams.director_email) → director (Directeur d'equipe)
//   3. systemCRole === 'GESTIONNAIRE'       → manager   (Gestionnaire)
//   4. systemCRole CONNU (null | UTILISATEUR | autre) → employee
//   5. systemCRole ABSENT (undefined)       → null      (information non portee)
//
// Contrat systemCRole (DOCTRINE : jamais afficher un role FAUX — pire qu'un trou) :
//   - `undefined` / cle absente = l'appelant N'A PAS la donnee Systeme C
//     → on peut encore conclure `director` si isTeamDirector, sinon `null`.
//     JAMAIS `employee` (un GESTIONNAIRE sans champ dans le DTO ne doit PAS
//     s'afficher « Employe »).
//   - `null` = information CONNUE : aucun role GEST dans ug.UserCompanyBcRoles
//     → director si isTeamDirector, sinon employee.
//   - 'ADMIN' | 'GESTIONNAIRE' | 'UTILISATEUR' = valeur connue du join UCBR.
//
// La qualite director DERIVE de gest.Teams.director_email — pas un champ profil,
// pas d'ecriture ug.UserCompanyBcRoles.

export type DerivedPersonRole = 'admin' | 'manager' | 'director' | 'employee';

export type SystemCRoleInput =
  | 'ADMIN'
  | 'GESTIONNAIRE'
  | 'UTILISATEUR'
  | string
  | null
  | undefined;

export interface DerivePersonRoleInput {
  /**
   * Role Systeme C GEST.
   * - omit / `undefined` = non porte par le DTO (ne pas conclure employee)
   * - `null` = porte et connu : aucun role C
   */
  systemCRole?: SystemCRoleInput;
  /** True si l'email figure dans au moins un gest.Teams.director_email actif. */
  isTeamDirector?: boolean;
}

/**
 * Derive le role d'affichage.
 * Retourne `null` quand systemCRole n'est pas porte et qu'on ne peut pas
 * conclure autrement (ex. directeur d'equipe) — l'UI doit alors laisser un
 * trou plutot que d'afficher « Employe ».
 */
export function derivePersonRole(input: DerivePersonRoleInput): DerivedPersonRole | null {
  if (input.systemCRole === 'ADMIN') return 'admin';
  if (input.isTeamDirector) return 'director';
  if (input.systemCRole === 'GESTIONNAIRE') return 'manager';
  // Information non portee : ne PAS inventer employee.
  if (input.systemCRole === undefined) return null;
  // Information connue (null / UTILISATEUR / hors catalogue) → employee.
  return 'employee';
}

/** Cle i18n FR+EN pour le libelle du role derive (`gest:personRole.*`). */
export function personRoleI18nKey(role: DerivedPersonRole): `gest:personRole.${DerivedPersonRole}` {
  return `gest:personRole.${role}`;
}

/**
 * Titre libre affiche seulement s'il est renseigne (complement secondaire).
 * Retourne null si vide / espaces — l'UI ne doit rien rendre dans ce cas.
 */
export function optionalJobTitle(jobTitle: string | null | undefined): string | null {
  if (jobTitle == null) return null;
  const trimmed = jobTitle.trim();
  return trimmed.length > 0 ? trimmed : null;
}

/**
 * Compose la sous-ligne d'affichage : role derive (si connu) + job_title facultatif.
 * Si le role est inconnu (null), ne rend QUE le job_title — jamais un faux « Employe ».
 */
export function composePersonRoleSubtitle(
  roleLabel: string | null,
  jobTitle: string | null | undefined,
): string | null {
  const job = optionalJobTitle(jobTitle);
  if (roleLabel && job) return `${roleLabel} · ${job}`;
  if (roleLabel) return roleLabel;
  return job;
}
